/**
 * Bent gallery community creations carousel.
 * - Cards laid out on a parabolic arc with rotation
 * - Infinite horizontal loop (modulo wrap)
 * - Mouse wheel + drag + touch all scroll the track
 * - On mobile (<= 768px), falls back to native horizontal scroll-snap
 */
class CommunityCreationsGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-stage]');
    this.track = this.querySelector('[data-track]');
    if (!this.stage || !this.track) return;

    this.bindSoundToggles();

    this.mobileMQ = window.matchMedia('(max-width: 768px)');
    this.handleMQChange = () => this.setupMode();
    this.mobileMQ.addEventListener('change', this.handleMQChange);
    this.setupMode();
  }

  disconnectedCallback() {
    this.teardownBent();
    this.mobileMQ?.removeEventListener('change', this.handleMQChange);
  }

  setupMode() {
    if (this.mobileMQ.matches) {
      this.teardownBent();
      this.stage.dataset.mode = 'native';
    } else {
      this.stage.dataset.mode = 'bent';
      this.setupBent();
    }
  }

  bindSoundToggles() {
    this.querySelectorAll('[data-sound-toggle]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();
        const trigger = /** @type {HTMLElement} */ (event.currentTarget);
        const card = trigger.closest('.community-creations__card');
        const video = card?.querySelector('video');
        if (!video) return;

        const willUnmute = video.muted;
        if (willUnmute) {
          this.querySelectorAll('video').forEach((v) => { v.muted = true; });
          this.querySelectorAll('[data-sound-toggle]').forEach((b) => {
            /** @type {HTMLElement} */ (b).dataset.active = 'false';
          });
        }
        video.muted = !willUnmute;
        trigger.dataset.active = willUnmute ? 'true' : 'false';
      });
    });
  }

  setupBent() {
    if (this.bentReady) return;
    this.bentReady = true;

    const cards = Array.from(this.track.querySelectorAll('.community-creations__card'));
    if (!cards.length) return;
    this.cards = cards;

    const cs = getComputedStyle(this);
    this.BEND_DEPTH = parseFloat(cs.getPropertyValue('--community-bend-depth')) || 80;
    this.TILT_DEG = parseFloat(cs.getPropertyValue('--community-tilt-deg')) || 12;
    this.SCROLL_EASE = parseFloat(cs.getPropertyValue('--community-scroll-ease')) || 0.08;
    this.WHEEL_SPEED = parseFloat(cs.getPropertyValue('--community-wheel-speed')) || 3;
    this.DRAG_SPEED = parseFloat(cs.getPropertyValue('--community-drag-speed')) || 2.2;

    this.scroll = { current: 0, target: 0 };
    this.isDown = false;
    this.startX = 0;
    this.startScroll = 0;

    this.onDown = this.onDown.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onResize = this.onResize.bind(this);
    this.update = this.update.bind(this);

    this.measure();
    this.raf = requestAnimationFrame(this.update);

    this.stage.addEventListener('mousedown', this.onDown);
    window.addEventListener('mousemove', this.onMove);
    window.addEventListener('mouseup', this.onUp);
    this.stage.addEventListener('touchstart', this.onDown, { passive: true });
    window.addEventListener('touchmove', this.onMove, { passive: true });
    window.addEventListener('touchend', this.onUp);
    this.stage.addEventListener('wheel', this.onWheel, { passive: true });
    window.addEventListener('resize', this.onResize);
  }

  teardownBent() {
    if (!this.bentReady) return;
    this.bentReady = false;

    cancelAnimationFrame(this.raf);
    this.stage.removeEventListener('mousedown', this.onDown);
    window.removeEventListener('mousemove', this.onMove);
    window.removeEventListener('mouseup', this.onUp);
    this.stage.removeEventListener('touchstart', this.onDown);
    window.removeEventListener('touchmove', this.onMove);
    window.removeEventListener('touchend', this.onUp);
    this.stage.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('resize', this.onResize);

    this.cards?.forEach((c) => { c.style.transform = ''; c.style.width = ''; });
  }

  pickCardWidth(w) {
    if (w >= 1400) return 240;
    if (w >= 1200) return 210;
    if (w >= 900) return 190;
    if (w >= 640) return 170;
    return 150;
  }

  measure() {
    this.stageW = this.stage.clientWidth;
    this.stageH = this.stage.clientHeight;
    this.halfW = this.stageW / 2;
    this.cardW = this.pickCardWidth(this.stageW);
    this.gap = this.stageW >= 1400 ? 60 : this.stageW >= 900 ? 48 : 36;
    this.total = (this.cardW + this.gap) * this.cards.length;

    this.cards.forEach((c) => { c.style.width = this.cardW + 'px'; });
  }

  wrap(v, m) {
    return ((v % m) + m) % m;
  }

  update() {
    this.scroll.current += (this.scroll.target - this.scroll.current) * this.SCROLL_EASE;

    const baseY = 20;
    for (let i = 0; i < this.cards.length; i++) {
      const card = this.cards[i];
      const baseCenter = i * (this.cardW + this.gap) + this.cardW / 2;
      let rel = baseCenter - this.scroll.current;
      rel = this.wrap(rel + this.total / 2, this.total) - this.total / 2;

      const norm = Math.max(-1.2, Math.min(1.2, rel / this.halfW));
      const yArc = norm * norm * this.BEND_DEPTH;
      const rot = norm * this.TILT_DEG;

      const tx = this.halfW + rel - this.cardW / 2;
      const ty = baseY + yArc;
      card.style.transform = 'translate(' + tx + 'px, ' + ty + 'px) rotate(' + rot + 'deg)';
    }

    this.raf = requestAnimationFrame(this.update);
  }

  getX(e) {
    return e.touches ? e.touches[0].clientX : e.clientX;
  }

  onDown(e) {
    if (e.target.closest('[data-sound-toggle]')) return;
    this.isDown = true;
    this.startX = this.getX(e);
    this.startScroll = this.scroll.target;
    this.stage.classList.add('is-dragging');
  }

  onMove(e) {
    if (!this.isDown) return;
    const dx = this.getX(e) - this.startX;
    this.scroll.target = this.startScroll - dx * this.DRAG_SPEED;
  }

  onUp() {
    this.isDown = false;
    this.stage.classList.remove('is-dragging');
  }

  onWheel(e) {
    const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    this.scroll.target += delta * this.WHEEL_SPEED;
  }

  onResize() {
    this.measure();
  }
}

if (!customElements.get('community-creations-gallery')) {
  customElements.define('community-creations-gallery', CommunityCreationsGallery);
}
